import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowPathIcon, MagnifyingGlassIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import PermissionsContainer, { useResolvedAccessLevel } from '../../components/PermissionsContainer';
import { usePermissionsMode } from '../../contexts/PermissionsModeContext';
import FilterBar from '../../components/FilterBar';
import SingleSelect from '../../components/SingleSelect';
import safetySystemService from '../../services/safetySystem.service';
import type { MaintenanceListParams, MaintenanceReportRow } from '../../types/safetySystem';
import MaintenanceDrawer from './components/MaintenanceDrawer';
import { MONTH_OPTIONS, STATUS_OPTIONS, yearOptions } from './components/maintenanceForm';
import { MAINTENANCE_COLUMNS, MAINTENANCE_DATE_COLUMNS, MAINTENANCE_DEFAULT_HIDDEN } from './SafetyMaintenance.columns';

/**
 * Maintenance Reports grid — port of the V1 Safety monthly maintenance report search.
 * Filter state lives in the URL (open, status, unitnumber, year, month, include_deleted)
 * so the Hub can deep-link (`/safety/maintenance-reports?open=1`); `open=<recno>` (any
 * value other than 1) opens that report's drawer instead.
 */

const rowId = (r: MaintenanceReportRow) => String(r.maintreportrecno ?? r.recno);

export default function SafetyMaintenance() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setPageComponents } = usePermissionsMode();
  const canEdit = useResolvedAccessLevel('safety-system-unit-edit', true) >= 2;

  const pageComponentsList = useMemo(() => [
    { id: 'safety-system-maintenance-table', name: 'Safety Maintenance Reports Table' },
    { id: 'safety-system-unit-edit', name: 'Unit Edit' },
  ], []);
  useEffect(() => {
    setPageComponents(pageComponentsList);
    return () => setPageComponents([]);
  }, [pageComponentsList, setPageComponents]);

  // ---- URL state -----------------------------------------------------------
  const openRaw = searchParams.get('open') || '';
  const openOnly = openRaw === '1';
  const openRecno = /^\d+$/.test(openRaw) && openRaw !== '1' ? Number(openRaw) : null;
  const status = searchParams.get('status') || '';
  const unitnumber = searchParams.get('unitnumber') || '';
  const year = searchParams.get('year') || '';
  const month = searchParams.get('month') || '';
  const includeDeleted = searchParams.get('include_deleted') === '1';

  const setParams = useCallback((patch: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [draft, setDraft] = useState(unitnumber);
  useEffect(() => { setDraft(unitnumber); }, [unitnumber]);
  const commitSearch = () => setParams({ unitnumber: draft.trim() });

  // ---- Data ----------------------------------------------------------------
  const [rows, setRows] = useState<MaintenanceReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadTick, setReloadTick] = useState(0);

  const listParams = useMemo<MaintenanceListParams>(() => ({
    open: openOnly, status, unitnumber, year, month, include_deleted: includeDeleted,
  }), [openOnly, status, unitnumber, year, month, includeDeleted]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    safetySystemService.listMaintenance(listParams, controller.signal)
      .then((res) => { if (!controller.signal.aborted) setRows(res.data ?? []); })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setRows([]);
        setError(e?.response?.data?.detail || 'Unable to load maintenance reports. Retry or check your access with an administrator.');
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [listParams, reloadTick]);

  // ---- Drawer --------------------------------------------------------------
  // `drawer` = closed | { recno: number | null } (null = New Report).
  const [drawer, setDrawer] = useState<{ recno: number | null } | null>(null);
  useEffect(() => {
    if (openRecno !== null) setDrawer({ recno: openRecno });
  }, [openRecno]);

  const closeDrawer = () => {
    setDrawer(null);
    if (openRecno !== null) setParams({ open: null });
  };

  const years = useMemo(() => yearOptions(), []);

  return (
    <main className="flex h-[calc(100vh-7rem)] min-h-[450px] flex-col gap-2 overflow-hidden p-2 sm:p-4 text-gray-900 dark:text-gray-100">
      <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Maintenance Reports</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Monthly driver maintenance reports. Click a row to open the report.</p>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button
              type="button"
              onClick={() => setDrawer({ recno: null })}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg hover:bg-accent-hover"
            >
              <PlusIcon width={16} aria-hidden="true" />
              New Report
            </button>
          )}
          <button
            type="button"
            onClick={() => setReloadTick((t) => t + 1)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-rule px-3 py-1.5 text-sm hover:bg-highlight disabled:cursor-not-allowed disabled:text-disabled"
          >
            <ArrowPathIcon width={16} aria-hidden="true" />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </header>
      {error && <div role="alert" className="shrink-0 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{error}</div>}

      <FilterBar wrap label="" className="shrink-0">
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" aria-label="Open only" checked={openOnly}
            onChange={(e) => setParams({ open: e.target.checked ? '1' : null })}
            className="h-4 w-4 rounded border-rule-strong" />
          Open only
        </label>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" aria-label="Include deleted" checked={includeDeleted}
            onChange={(e) => setParams({ include_deleted: e.target.checked ? '1' : null })}
            className="h-4 w-4 rounded border-rule-strong" />
          Include deleted
        </label>
        <FilterBar.Divider />
        <FilterBar.Group label="Status">
          <SingleSelect ariaLabel="Status" value={status} onChange={(v) => setParams({ status: v })}
            options={STATUS_OPTIONS} size="sm" className="w-48" usePortal showClearOption clearOptionLabel="All" />
        </FilterBar.Group>
        <FilterBar.Group label="Year">
          <SingleSelect ariaLabel="Year" value={year} onChange={(v) => setParams({ year: v })}
            options={years} size="sm" className="w-28" usePortal showClearOption clearOptionLabel="All" />
        </FilterBar.Group>
        <FilterBar.Group label="Month">
          <SingleSelect ariaLabel="Month" value={month} onChange={(v) => setParams({ month: v })}
            options={MONTH_OPTIONS} size="sm" className="w-36" usePortal showClearOption clearOptionLabel="All" />
        </FilterBar.Group>
        <FilterBar.Divider />
        <input
          type="text"
          aria-label="Unit #"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commitSearch(); }}
          placeholder="Unit #…"
          className="h-8 w-32 rounded-md border border-rule-strong bg-field px-2 text-sm"
        />
        <button type="button" onClick={commitSearch}
          className="inline-flex h-8 items-center gap-1 rounded-md bg-accent px-3 text-sm font-medium text-accent-fg hover:bg-accent-hover">
          <MagnifyingGlassIcon width={16} aria-hidden="true" />Search
        </button>
        {unitnumber && (
          <button type="button" onClick={() => setParams({ unitnumber: null })}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-rule px-2 text-sm hover:bg-highlight">
            <XMarkIcon width={14} aria-hidden="true" />Clear unit
          </button>
        )}
      </FilterBar>

      <PermissionsContainer componentId="safety-system-maintenance-table" componentName="Safety Maintenance Reports Table" defaultHidden className="min-h-0 flex-1">
        <TanTable<MaintenanceReportRow>
          data={rows}
          columns={MAINTENANCE_COLUMNS}
          loading={loading}
          fillContainer
          cardTitle="Maintenance Reports"
          settingsKey="safety-maintenance"
          fieldName="safety-maintenance"
          searchPlaceholder="Filter results…"
          enableColumnFilters
          enableExcelExport
          enableShowHideCols
          enableRowsPerPage
          enableZebraStriping
          defaultSorting={[{ id: 'maintreportrecno', desc: true }]}
          defaultColumnVisibility={MAINTENANCE_DEFAULT_HIDDEN}
          defaultPageSize={100}
          dateColumns={MAINTENANCE_DATE_COLUMNS}
          onRowClick={(r) => { const id = Number(r.maintreportrecno ?? r.recno); if (id) setDrawer({ recno: id }); }}
          getRowId={rowId}
        />
      </PermissionsContainer>

      <MaintenanceDrawer
        isOpen={drawer !== null}
        onClose={closeDrawer}
        recno={drawer?.recno ?? null}
        onSaved={() => setReloadTick((t) => t + 1)}
      />
    </main>
  );
}
